import { useState } from 'react';
import { Badge, Group, Paper, Table, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatPenceToPounds } from 'src/secret-menu/billing/billingUtils';
import {
  applyLens,
  ENGAGEMENT_ORDER,
  engagementOf,
  formatLensValue,
  trendOf,
  type Lens,
} from './lenses';
import { billingFor, formatPercent, percentOfStated, type RevenueModel } from './revenue';
import { previousMonth, type Firm, type UsageModel } from './usageModel';

type Props = {
  model: UsageModel;
  month: string;
  lens: Lens;
  today: Date;
  revenue: RevenueModel | null;
  onSelectFirm: (name: string) => void;
};

type SortKey =
  | 'name'
  | 'clients'
  | 'usage'
  | 'prev'
  | 'mrr'
  | 'loaded'
  | 'engagement'
  | 'trend'
  | 'last';

type Row = {
  firm: Firm;
  clients: number;
  usage: number;
  displayUsage: number | null;
  previous: number;
  mrr: number | null;
  loaded: number | null;
  engagement: ReturnType<typeof engagementOf>;
  trend: ReturnType<typeof trendOf>;
};

const TREND_ORDER: Record<string, number> = { Up: 3, New: 2, Flat: 1, Down: 0 };

export default function FirmsPanel({ model, month, lens, today, revenue, onSelectFirm }: Props) {
  const [sort, setSort] = useState<SortKey>('usage');
  const [descending, setDescending] = useState(true);
  const [search, setSearch] = useState('');

  const prev = previousMonth(model, month);
  const term = search.trim().toLowerCase();

  const rows: Row[] = model.firms
    .filter(firm => term === '' || firm.name.toLowerCase().includes(term))
    .map(firm => {
      const counts = firm.series.get(month);
      const usage = counts?.usage ?? 0;
      const billing = billingFor(revenue, firm.name);
      return {
        firm,
        clients: firm.clientsAt.get(month) ?? 0,
        usage,
        displayUsage: applyLens(usage, month, lens, { model, today, firm }),
        previous: prev ? (firm.series.get(prev)?.usage ?? 0) : 0,
        mrr: billing?.monthlyBillPence ?? null,
        loaded: billing
          ? percentOfStated(billing.billableUnitCount, billing.statedBillableUnits)
          : null,
        engagement: engagementOf(usage, counts?.newClients ?? 0),
        trend: trendOf(firm, month, model),
      };
    });

  rows.sort((a, b) => {
    const direction = descending ? -1 : 1;
    let comparison = 0;

    if (sort === 'name') comparison = a.firm.name.localeCompare(b.firm.name);
    else if (sort === 'clients') comparison = a.clients - b.clients;
    else if (sort === 'usage') comparison = a.usage - b.usage;
    else if (sort === 'prev') comparison = a.previous - b.previous;
    else if (sort === 'mrr') comparison = (a.mrr ?? 0) - (b.mrr ?? 0);
    else if (sort === 'loaded') comparison = (a.loaded ?? -1) - (b.loaded ?? -1);
    else if (sort === 'engagement')
      comparison = ENGAGEMENT_ORDER[a.engagement] - ENGAGEMENT_ORDER[b.engagement];
    else if (sort === 'trend') comparison = TREND_ORDER[a.trend] - TREND_ORDER[b.trend];
    else comparison = a.firm.last.localeCompare(b.firm.last);

    if (comparison === 0) comparison = a.usage - b.usage;
    return comparison * direction;
  });

  function toggleSort(key: SortKey) {
    if (key === sort) {
      setDescending(current => !current);
      return;
    }
    setSort(key);
    setDescending(true);
  }

  function sortMarker(key: SortKey) {
    if (sort !== key) return '';
    return descending ? ' ↓' : ' ↑';
  }

  function header(key: SortKey, label: string, alignRight = false) {
    return (
      <Table.Th
        ta={alignRight ? 'right' : undefined}
        onClick={() => toggleSort(key)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        {label}
        {sortMarker(key)}
      </Table.Th>
    );
  }

  return (
    <Paper p="md" withBorder radius="md">
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Text fw={FONT_WEIGHTS.semibold}>
          {rows.length} {rows.length === 1 ? 'firm' : 'firms'} · {model.monthLabels.get(month)}
        </Text>
        <TextInput
          placeholder="Search firms"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={event => setSearch(event.currentTarget.value)}
          w={240}
        />
      </Group>

      <Table.ScrollContainer minWidth={1000}>
        <Table highlightOnHover verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              {header('name', 'Firm')}
              {header('clients', 'Clients', true)}
              {header('usage', 'ABC excluding adding new clients', true)}
              {header('prev', 'Previous', true)}
              {header('mrr', 'MRR', true)}
              {header('loaded', 'Loaded', true)}
              {header('engagement', 'Engagement', true)}
              {header('trend', 'Trend', true)}
              {header('last', 'Last active', true)}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(row => (
              <Table.Tr
                key={row.firm.name}
                onClick={() => onSelectFirm(row.firm.name)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>{row.firm.name}</Table.Td>
                <Table.Td ta="right">{row.clients.toLocaleString('en-GB')}</Table.Td>
                <Table.Td ta="right" fw={FONT_WEIGHTS.semibold}>
                  {formatLensValue(row.displayUsage, lens)}
                </Table.Td>
                <Table.Td ta="right">{row.previous.toLocaleString('en-GB')}</Table.Td>
                <Table.Td ta="right">
                  {row.mrr === null ? (
                    <Text size="textXs" c="dimmed">
                      Not billed
                    </Text>
                  ) : (
                    formatPenceToPounds(row.mrr)
                  )}
                </Table.Td>
                <Table.Td ta="right">{formatPercent(row.loaded)}</Table.Td>
                <Table.Td ta="right">
                  <Badge size="sm" variant="light" color="gray">
                    {row.engagement}
                  </Badge>
                </Table.Td>
                <Table.Td ta="right">
                  <Badge size="sm" variant="light">
                    {row.trend}
                  </Badge>
                </Table.Td>
                <Table.Td ta="right">{model.monthLabels.get(row.firm.last)}</Table.Td>
              </Table.Tr>
            ))}
            {rows.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={9}>
                  <Text size="textXs" c="dimmed">
                    No firms match that search.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Paper>
  );
}
